import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../lib/seo';

export function Terms({ settings }) {
  const phone = settings?.phone || "82 82 10 10";
  const email = settings?.email || "kontakt@fodtid.dk";
  const cvr = settings?.cvr || "44137992";
  const address = settings?.address || "Perikumhaven 63A, 2730 Herlev";

  return (
    <>
      <SEO 
        title="Salgs- og leveringsbetingelser | Fodtid"
        description="Læs salgs- og leveringsbetingelser for Fodtid ApS vedrørende booking, aflysning, kørsel, betaling og abonnement."
      />

      {/* Header */}
      <section className="bg-canvas py-14 sm:py-18 border-b border-brand-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center space-y-3">
          <span className="text-xs uppercase font-bold tracking-widest text-brand-charcoal bg-surface px-3.5 py-1 rounded-full border border-brand-border">
            Handelsbetingelser
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-brand-charcoal">
            Salgs- og leveringsbetingelser
          </h1>
          <p className="text-xs sm:text-sm text-brand-muted font-semibold">
            Fodtid ApS • Telefon: <span className="whitespace-nowrap">{phone}</span> • CVR: {cvr}
          </p>
        </div>
      </section>

      {/* Body Terms */}
      <section className="py-14 sm:py-20 bg-canvas border-b border-brand-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 space-y-10 text-brand-charcoal leading-relaxed text-sm">
          
          <div className="space-y-3">
            <h2 className="font-serif text-xl font-bold text-brand-charcoal">
              1. Aftaleindgåelse
            </h2>
            <p className="text-brand-muted">
              Når du booker en tid hos Fodtid ApS, indgår du en bindende aftale om køb af fodplejeydelser. Bekræftelse sker via e-mail, sms eller telefon.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="font-serif text-xl font-bold text-brand-charcoal">
              2. Aflysning og ændring af aftaler
            </h2>
            <p className="text-brand-muted">
              Aflysning eller ændring af din aftale skal ske senest 24 timer før den aftalte tid. Ved for sen aflysning eller udeblivelse forbeholder vi os retten til at opkræve fuld pris for den planlagte behandling.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="font-serif text-xl font-bold text-brand-charcoal">
              3. Ankomsttidspunkt og forsinkelser
            </h2>
            <p className="text-brand-muted">
              Vores behandlere bestræber sig på at komme til tiden, men grundet uforudsigelige forhold som trafik og vejforhold, kan der forekomme afvigelser i ankomsttiden på op til ± 60 minutter i forhold til det aftalte tidspunkt. Du bedes derfor være tilgængelig i dette tidsrum.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="font-serif text-xl font-bold text-brand-charcoal">
              4. Kørsel og geografisk dækning
            </h2>
            <ul className="space-y-2 list-disc pl-5 text-brand-muted">
              <li>I postnumrene 2000 – 2990 er kørsel inkluderet i prisen.</li>
              <li>I postnumrene 0900 – 1999 og 3000 – 5000 pålægges et kørselsgebyr på 150 kr. pr. besøg.</li>
            </ul>
            <p className="text-brand-muted">
              Hvis du er i tvivl om, hvordan dit postnummer påvirker prisen, er du velkommen til at kontakte os inden booking.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="font-serif text-xl font-bold text-brand-charcoal">
              5. Betaling
            </h2>
            <p className="text-brand-muted">Du kan betale for din behandling på følgende måder:</p>
            <ul className="space-y-1.5 list-disc pl-5 text-brand-muted">
              <li>Kontant</li>
              <li>MobilePay: 478470</li>
              <li>Faktura (efter aftale)</li>
              <li>Abonnementsordning</li>
            </ul>
            
            <div className="bg-surface/60 p-6 rounded-2xl border border-brand-border space-y-2.5 mt-4">
              <h3 className="font-serif font-bold text-base text-brand-charcoal">Abonnementsordning:</h3>
              <p className="text-brand-muted">
                Ved tilmelding til vores abonnementsordning betaler du 119 kr. pr. måned for løbende adgang til vores ydelser efter nærmere aftale. (4 x årligt)
              </p>
              <ul className="space-y-1.5 list-disc pl-5 text-xs text-brand-muted">
                <li>Abonnementet er bindende i 12 måneder.</li>
                <li>Det kan opsiges skriftligt i 11. måned, da der er 1 måneds opsigelse.</li>
                <li>Du kan vælge at betale hele beløbet på én gang eller via månedlig betaling gennem vores netbaserede betalingsløsning.</li>
              </ul>
              <p className="text-xs text-brand-muted pt-1">
                Du vil modtage en kvittering for hver betaling, og vi bruger sikre og godkendte betalingssystemer.
              </p>
              <div className="pt-2">
                <a
                  href="https://assets.auxodev.dk/assets/d4428667-cde9-4c31-9224-88278506a01a/abonnementsbetingelser.pdf"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-bold text-brand-charcoal hover:text-brand-accent underline transition-colors"
                >
                  <i className="fa-solid fa-file-pdf text-brand-accent" aria-hidden="true" />
                  <span>Hent de officielle abonnementsbetingelser som PDF</span>
                </a>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h2 className="font-serif text-xl font-bold text-brand-charcoal">
              6. Klager og reklamation
            </h2>
            <p className="text-brand-muted">
              Er du ikke tilfreds med den udførte behandling, bedes du kontakte os hurtigst muligt og senest 48 timer efter behandlingen, så vi sammen kan finde en god løsning.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="font-serif text-xl font-bold text-brand-charcoal">
              7. Kontaktoplysninger
            </h2>
            <div className="p-6 bg-surface/40 rounded-2xl border border-brand-border space-y-1 text-xs text-brand-charcoal">
              <div className="font-bold text-sm">Fodtid ApS</div>
              <div>Adresse: {address}</div>
              <div>CVR-nr.: {cvr}</div>
              <div>Telefon: <a href={`tel:${phone.replace(/\s+/g, '')}`} className="font-semibold underline text-brand-charcoal">{phone}</a></div>
              <div>E-mail: <a href={`mailto:${email}`} className="font-semibold underline text-brand-charcoal">{email}</a></div>
            </div>
          </div>

          <div className="pt-8 border-t border-brand-border flex items-center justify-between">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand-charcoal hover:text-brand-accent transition-colors"
            >
              <i className="fa-solid fa-arrow-left" aria-hidden="true" />
              <span>Tilbage til forsiden</span>
            </Link>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 bg-brand-charcoal hover:bg-black text-canvas font-bold px-6 py-3 rounded-full text-xs uppercase tracking-wider shadow transition-colors"
            >
              <span>Bestil tid online</span>
            </Link>
          </div>

        </div>
      </section>
    </>
  );
}
